import React from 'react';
import { Link } from 'react-router-dom';

interface MarkdownRendererProps {
  content: string;
}

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  const regex = /(\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let part = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    if (match[2] && match[3]) {
      const href = match[3];
      const label = match[2];
      if (href.startsWith('/')) {
        nodes.push(
          <Link key={`${keyPrefix}-l${part++}`} to={href} className="text-emerald-600 hover:text-emerald-700 font-medium underline">
            {label}
          </Link>
        );
      } else {
        nodes.push(
          <a key={`${keyPrefix}-a${part++}`} href={href} className="text-emerald-600 hover:text-emerald-700 font-medium underline" rel="noopener noreferrer">
            {label}
          </a>
        );
      }
    } else if (match[4]) {
      nodes.push(<strong key={`${keyPrefix}-b${part++}`}>{match[4]}</strong>);
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes.length > 0 ? nodes : [text];
}

export default function MarkdownRenderer({ content }: MarkdownRendererProps) {
  const parseMarkdown = (text: string) => {
    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];
    let key = 0;
    let skippedTitle = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      if (line === '') {
        continue;
      }

      if (line.startsWith('# ')) {
        if (!skippedTitle) {
          skippedTitle = true;
          continue;
        }
        elements.push(
          <h2 key={key++} className="text-3xl font-bold text-gray-900 mt-8 mb-4 leading-tight">
            {renderInline(line.substring(2), `h-${key}`)}
          </h2>
        );
      } else if (line.startsWith('## ')) {
        elements.push(
          <h2 key={key++} className="text-3xl font-bold text-gray-900 mt-8 mb-4 leading-tight">
            {renderInline(line.substring(3), `h2-${key}`)}
          </h2>
        );
      } else if (line.startsWith('### ')) {
        elements.push(
          <h3 key={key++} className="text-2xl font-bold text-gray-900 mt-6 mb-3 leading-tight">
            {renderInline(line.substring(4), `h3-${key}`)}
          </h3>
        );
      } else if (line.startsWith('#### ')) {
        elements.push(
          <h4 key={key++} className="text-xl font-bold text-gray-900 mt-4 mb-2 leading-tight">
            {renderInline(line.substring(5), `h4-${key}`)}
          </h4>
        );
      } else if (line.startsWith('- ') || line.startsWith('- [')) {
        const listItems: React.ReactNode[] = [];
        let j = i;

        while (j < lines.length && lines[j].trim().startsWith('- ')) {
          const itemText = lines[j].trim().substring(2);
          listItems.push(
            <li key={j} className="flex items-start mb-2">
              <span className="text-emerald-600 mr-3 mt-1">•</span>
              <span className="text-gray-700 leading-relaxed">{renderInline(itemText, `li-${j}`)}</span>
            </li>
          );
          j++;
        }

        elements.push(
          <ul key={key++} className="my-6 space-y-2">
            {listItems}
          </ul>
        );
        i = j - 1;
      } else if (/^\d+\.\s/.test(line)) {
        const listItems: React.ReactNode[] = [];
        let j = i;

        while (j < lines.length && /^\d+\.\s/.test(lines[j].trim())) {
          const itemText = lines[j].trim().replace(/^\d+\.\s/, '');
          listItems.push(
            <li key={j} className="flex items-start mb-2">
              <span className="text-emerald-600 mr-3 mt-1 font-semibold">
                {lines[j].trim().match(/^\d+/)?.[0]}.
              </span>
              <span className="text-gray-700 leading-relaxed">{renderInline(itemText, `ol-${j}`)}</span>
            </li>
          );
          j++;
        }

        elements.push(
          <ol key={key++} className="my-6 space-y-2">
            {listItems}
          </ol>
        );
        i = j - 1;
      } else if (line.startsWith('✅')) {
        elements.push(
          <div key={key++} className="flex items-center my-2">
            <span className="text-green-600 mr-3">✅</span>
            <span className="text-gray-700">{renderInline(line.substring(2).trim(), `ok-${key}`)}</span>
          </div>
        );
      } else if (line.startsWith('❌')) {
        elements.push(
          <div key={key++} className="flex items-center my-2">
            <span className="text-red-600 mr-3">❌</span>
            <span className="text-gray-700">{renderInline(line.substring(2).trim(), `no-${key}`)}</span>
          </div>
        );
      } else {
        elements.push(
          <p key={key++} className="text-gray-700 leading-relaxed my-4 text-lg">
            {renderInline(line, `p-${key}`)}
          </p>
        );
      }
    }

    return elements;
  };

  return (
    <div className="prose prose-xl max-w-none">
      {parseMarkdown(content)}
    </div>
  );
}
